import * as M from "../Model"
import * as nav from "elm-ts/lib/Navigation"
import { cmd } from "elm-ts"
import { pipe, flow } from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as http from "elm-ts/lib/Http"
import * as Http from "../../Http"
import { API_BASE_URL } from "../../runtimeVariables"
import {
  IO,
  Variant,
  DealOverview,
  DealViewings,
  DealRentals,
  DealFeedback,
  Dashboard,
  Account,
} from "@flypengine/myFlyp-lib"
import * as A from "fp-ts/Array"
import * as E from "fp-ts/Either"
import * as updateOverview from "./updateOverview"
import * as updateViewings from "./updateViewings"
import * as updateFeedback from "./updateFeedback"
import * as updateRentals from "./updateRentals"
import { tuple } from "fp-ts"
import { overviewInitModel } from "../Model/overviewModel"
import * as R from "fp-ts-routing"
import * as Router from "../../routes"
import dayjs from "dayjs"
import { sequenceS } from "fp-ts/lib/Apply"
import { initFeedbackModel } from "../Model/feedbackModel"
import { initViewingsCalendarModel } from "../Model/viewingsModel"
import { initRentalsModel } from "../Model/rentalsModel"
import * as RentalsModel from "../Model/rentalsModel"
import { Types } from "@flypengine/lib-shared"

const defaultModel = (tab: O.Option<Router.Tab>): M.Model => ({
  tab,
  navigation: [],
  properties: E.left("Not Attempted"),
  dashboard: E.left("Not Attempted"),
  overviewModel: updateOverview.initModel,
  viewingsModel: updateViewings.initModel,
  feedbackModel: updateFeedback.initModel,
  rentalsModel: updateRentals.initModel,
})

export const initModel = (
  model: O.Option<M.Model>,
  tab: O.Option<Router.Tab>
) => O.getOrElse(() => defaultModel(tab))(model)

export const init: <msg>(
  config: M.Config<msg>
) => (model: O.Option<M.Model>) => [M.Model, cmd.Cmd<M.Msg>] =
  config => model => {
    switch (config.route._tag) {
      case "Home": {
        return [
          initModel(model, O.none),
          pipe(
            http.get(
              `${API_BASE_URL}/myFlyp/account`,
              flow(Account.decode, E.mapLeft(IO.validationErrorsToString))
            ),
            Http.sendRequest(config.user)(flow(M.Msg.initDashboard))
          ),
        ]
      }
      case "Dashboard": {
        const propcode = config.route.propcode
        return [
          initModel(model, O.some(config.route.tab)),
          pipe(
            model,
            O.match(
              () => cmd.none,
              m =>
                E.isLeft(m.properties)
                  ? cmd.batch([
                      cmd.of(M.Msg.accountReq()),
                      cmd.of(M.Msg.dashboardReq(propcode)),
                    ])
                  : pipe(propcode, M.Msg.dashboardReq, cmd.of)
            )
          ),
        ]
      }
      case "NotFound": {
        return [initModel(model, O.none), cmd.none]
      }
    }
  }

export const update: <msg>(
  config: M.Config<msg>
) => (msg: M.Msg) => (model: M.Model) => [M.Model, cmd.Cmd<msg>] =
  config => msg => model => {
    return pipe(
      msg,
      Variant.caseOf({
        [M._initDashboard]: response =>
          pipe(
            response,
            E.match(
              err => {
                switch (err._tag) {
                  case "BadStatus": {
                    return [
                      {
                        ...model,
                        properties:
                          err.response.status.code === 401 ||
                          err.response.status.code === 403
                            ? E.left("Not Authorized")
                            : E.left("Error"),
                      },
                      cmd.none,
                    ]
                  }

                  default: {
                    return [model, cmd.none]
                  }
                }
              },
              ({ properties }) => {
                return [
                  {
                    ...model,
                    tab: A.isEmpty(properties) ? O.none : model.tab,
                    properties: E.of(properties),
                  },
                  pipe(
                    properties,
                    A.head,
                    O.match(
                      () => cmd.none,
                      prop =>
                        pipe(
                          prop.products,
                          A.findFirst(p => p.type === "marketplace"),
                          O.match(
                            () =>
                              pipe(
                                prop.products,
                                A.findFirst(p => p.type === "transformations"),
                                O.match(
                                  () => cmd.none,
                                  ({ propertyCode }) =>
                                    nav.push(
                                      Router.dashboardMatch.formatter
                                        .run(R.Route.empty, {
                                          propcode: propertyCode,
                                          tab: "rentals",
                                        })
                                        .toString()
                                    )
                                )
                              ),
                            ({ propertyCode }) =>
                              nav.push(
                                Router.dashboardMatch.formatter
                                  .run(R.Route.empty, {
                                    propcode: propertyCode,
                                    tab: "overview",
                                  })
                                  .toString()
                              )
                          )
                        )
                    )
                  ),
                ]
              }
            )
          ),
        [M._tabChanged]: tab => {
          switch (config.route._tag) {
            case "Dashboard": {
              return [
                { ...model, tab: O.some(tab) },
                pipe(
                  model.dashboard,
                  E.match(
                    () => cmd.none,
                    ({ products }) => {
                      switch (tab) {
                        case "rentals": {
                          return pipe(
                            products,
                            A.findFirst(p => p.type === "transformations"),
                            O.match(
                              () => cmd.none,
                              ({ propertyCode }) =>
                                nav.push(
                                  Router.dashboardMatch.formatter
                                    .run(R.Route.empty, {
                                      propcode: propertyCode,
                                      tab,
                                    })
                                    .toString()
                                )
                            )
                          )
                        }

                        default: {
                          return pipe(
                            products,
                            A.findFirst(p => p.type === "marketplace"),
                            O.match(
                              () => cmd.none,
                              ({ propertyCode }) =>
                                nav.push(
                                  Router.dashboardMatch.formatter
                                    .run(R.Route.empty, {
                                      propcode: propertyCode,
                                      tab,
                                    })
                                    .toString()
                                )
                            )
                          )
                        }
                      }
                    }
                  )
                ),
              ]
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._dashboardChanged]: propcode => {
          switch (config.route._tag) {
            case "Dashboard": {
              const selection = pipe(
                model.dashboard,
                O.fromEither,
                O.match(
                  () => pipe(model.properties, O.fromEither, O.chain(A.head)),
                  () =>
                    pipe(
                      model.properties,
                      O.fromEither,
                      O.chain(props =>
                        pipe(
                          props,
                          A.findFirstMap(p =>
                            pipe(
                              p.products,
                              A.findFirstMap(prod =>
                                prod.propertyCode === propcode
                                  ? O.some(p)
                                  : O.none
                              )
                            )
                          )
                        )
                      )
                    )
                )
              )

              return [
                {
                  ...model,
                  dashboard: E.left("Loading"),
                },
                pipe(
                  selection,
                  O.match(
                    () => cmd.none,
                    p =>
                      pipe(
                        p.products,
                        A.findFirst(p => p.type === "marketplace"),
                        O.match(
                          () =>
                            pipe(
                              p.products,
                              A.findFirst(p => p.type === "transformations"),
                              O.match(
                                () => cmd.none,
                                ({ propertyCode }) =>
                                  nav.push(
                                    Router.dashboardMatch.formatter
                                      .run(R.Route.empty, {
                                        propcode: propertyCode,
                                        tab: "rentals",
                                      })
                                      .toString()
                                  )
                              )
                            ),
                          ({ propertyCode }) =>
                            nav.push(
                              Router.dashboardMatch.formatter
                                .run(R.Route.empty, {
                                  propcode: propertyCode,
                                  tab: "overview",
                                })
                                .toString()
                            )
                        )
                      )
                  )
                ),
              ]
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._updateNavigation]: newNavigation => [
          { ...model, navigation: newNavigation },
          cmd.none,
        ],
        [M._accountReq]: () => [
          model,
          pipe(
            http.get(
              `${API_BASE_URL}/myFlyp/account`,
              flow(Account.decode, E.mapLeft(IO.validationErrorsToString))
            ),
            Http.sendRequest(config.user)(
              flow(M.Msg.accountRes, config.wrapperMsg)
            )
          ),
        ],
        [M._accountRes]: response =>
          pipe(
            response,
            E.match(
              err => {
                switch (err._tag) {
                  case "BadStatus": {
                    return [
                      {
                        ...model,
                        properties:
                          err.response.status.code === 401 ||
                          err.response.status.code === 403
                            ? E.left("Not Authorized")
                            : E.left("Error"),
                      },
                      cmd.none,
                    ]
                  }

                  default: {
                    return [model, cmd.none]
                  }
                }
              },
              ({ properties }) => [
                {
                  ...model,
                  tab: A.isEmpty(properties) ? O.none : model.tab,
                  properties: E.of(properties),
                },
                cmd.none,
              ]
            )
          ),

        [M._dashboardReq]: propcode => [
          model,
          pipe(
            http.get(
              `${API_BASE_URL}/myFlyp/dashboard/${encodeURI(propcode)}`,
              flow(Dashboard.decode, E.mapLeft(IO.validationErrorsToString))
            ),
            Http.sendRequest(config.user)(
              flow(M.Msg.dashboardRes, config.wrapperMsg)
            )
          ),
        ],
        [M._dashboardRes]: response =>
          pipe(
            response,
            E.match(
              error => {
                switch (error._tag) {
                  case "BadStatus": {
                    if (
                      error.response.status.code === 401 ||
                      error.response.status.code === 403
                    ) {
                      return [
                        {
                          ...model,
                          dashboard: E.left("Not Authorized"),
                          overviewModel: E.left("Not Authorized"),
                          viewingsModel: {
                            ...updateViewings.initModel,
                            calendarModel: E.left("Not Authorized"),
                          },
                          feedbackModel: E.left("Not Authorized"),
                          rentalsModel: E.left("Not Authorized"),
                        },
                        cmd.none,
                      ]
                    }
                    return [
                      {
                        ...model,
                        dashboard: E.left("Error"),
                        overviewModel: E.left("Error"),
                        viewingsModel: {
                          ...updateViewings.initModel,
                          calendarModel: E.left("Error"),
                        },
                        feedbackModel: E.left("Error"),
                        rentalsModel: E.left("Error"),
                      },
                      cmd.none,
                    ]
                  }

                  default: {
                    return [model, cmd.none]
                  }
                }
              },
              dashboard => {
                const newModel = {
                  ...model,
                  dashboard: E.of(dashboard),
                  navigation: M.initNavigation(dashboard.features),
                }
                switch (config.route._tag) {
                  case "Home": {
                    return [
                      model,
                      pipe(
                        dashboard.products,
                        A.findFirst(p => p.type === "marketplace"),
                        O.match(
                          () =>
                            pipe(
                              dashboard.products,
                              A.findFirst(p => p.type === "transformations"),
                              O.match(
                                () => cmd.none,
                                ({ propertyCode }) =>
                                  nav.push(
                                    Router.dashboardMatch.formatter
                                      .run(R.Route.empty, {
                                        propcode: propertyCode,
                                        tab: "rentals",
                                      })
                                      .toString()
                                  )
                              )
                            ),
                          ({ propertyCode }) =>
                            nav.push(
                              Router.dashboardMatch.formatter
                                .run(R.Route.empty, {
                                  propcode: propertyCode,
                                  tab: "overview",
                                })
                                .toString()
                            )
                        )
                      ),
                    ]
                  }
                  case "Dashboard": {
                    switch (config.route.tab) {
                      case "overview": {
                        return [
                          newModel,
                          cmd.map(flow(M.Msg.overviewMsg, config.wrapperMsg))(
                            updateOverview.initCmd
                          ),
                        ]
                      }
                      case "viewings": {
                        return [
                          newModel,
                          cmd.map(flow(M.Msg.viewingsMsg, config.wrapperMsg))(
                            updateViewings.initCmd
                          ),
                        ]
                      }
                      case "feedback": {
                        return [
                          newModel,
                          cmd.map(flow(M.Msg.feedbackMsg, config.wrapperMsg))(
                            updateFeedback.initCmd
                          ),
                        ]
                      }
                      case "rentals": {
                        return [
                          newModel,
                          cmd.map(flow(M.Msg.rentalsMsg, config.wrapperMsg))(
                            updateRentals.initCmd
                          ),
                        ]
                      }
                    }
                  }

                  default: {
                    return [model, cmd.none]
                  }
                }
              }
            )
          ),
        [M._dealOverviewReq]: propcode => [
          { ...model, overviewModel: overviewInitModel(E.left("Loading")) },
          pipe(
            http.get(
              `${API_BASE_URL}/myFlyp/overview/${encodeURI(propcode)}`,
              flow(DealOverview.decode, E.mapLeft(IO.validationErrorsToString))
            ),
            Http.sendRequest(config.user)(
              flow(M.Msg.dealOverviewRes, config.wrapperMsg)
            )
          ),
        ],
        [M._dealOverviewRes]: result =>
          pipe(
            result,
            E.match(
              error => {
                switch (error._tag) {
                  case "BadStatus": {
                    return [
                      {
                        ...model,
                        tab: O.none,
                        overviewModel:
                          error.response.status.code === 401 ||
                          error.response.status.code === 403
                            ? overviewInitModel(E.left("Not Authorized"))
                            : overviewInitModel(E.left("Error")),
                      },
                      cmd.none,
                    ]
                  }

                  default: {
                    return [
                      {
                        ...model,
                        overviewModel: overviewInitModel(E.left("Error")),
                      },
                      cmd.none,
                    ]
                  }
                }
              },
              data => [
                {
                  ...model,
                  tab: O.some("overview"),
                  overviewModel: overviewInitModel(E.of(data)),
                },
                cmd.none,
              ]
            )
          ),
        [M._dealViewingsReq]: () => {
          switch (config.route._tag) {
            case "Dashboard": {
              const propcode = config.route.propcode

              const newModel: M.Model = pipe(
                model.viewingsModel.calendar,
                O.match(
                  () => model,
                  () => ({
                    ...model,
                    viewingsModel: {
                      ...model.viewingsModel,
                      calendarModel: E.left("Loading"),
                    },
                  })
                )
              )

              const newCmd = pipe(
                model.viewingsModel.calendar,
                O.match(
                  () => cmd.none,
                  calendar =>
                    pipe(
                      {
                        from: A.head(calendar.entries),
                        to: A.last(calendar.entries),
                      },
                      sequenceS(O.Apply),
                      O.match(
                        () => cmd.none,
                        ({ from, to }) =>
                          pipe(
                            http.get(
                              `${API_BASE_URL}/myFlyp/viewings/${encodeURI(
                                propcode
                              )}?start=${dayjs(from.date).format(
                                "YYYY-MM-DD"
                              )}&end=${dayjs(to.date).format("YYYY-MM-DD")}`,
                              flow(
                                DealViewings.decode,
                                E.mapLeft(IO.validationErrorsToString)
                              )
                            ),
                            Http.sendRequest(config.user)(
                              flow(M.Msg.dealViewingsRes, config.wrapperMsg)
                            )
                          )
                      )
                    )
                )
              )
              return [newModel, newCmd]
            }

            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._dealViewingsRes]: response => [
          pipe(
            response,
            E.match(
              (status): M.Model => {
                switch (status._tag) {
                  case "BadStatus": {
                    if (status.response.status.code === 403)
                      return {
                        ...model,
                        tab: O.none,
                        viewingsModel: {
                          ...model.viewingsModel,
                          calendarModel: initViewingsCalendarModel(
                            E.left("Not Authorized")
                          ),
                        },
                      }
                  }
                  default: {
                    return {
                      ...model,
                      viewingsModel: {
                        ...model.viewingsModel,
                        calendarModel: E.left("Error"),
                      },
                    }
                  }
                }
              },
              ({
                viewingsCalendar,
                upcomingViewing,
                calendar_availability,
                calendar_override,
                calendar_notice_period_hours,
                calendar_paused,
                product_type,
              }): M.Model => ({
                ...model,
                viewingsModel: {
                  ...model.viewingsModel,
                  calendarModel: initViewingsCalendarModel(
                    E.of({
                      upcoming: upcomingViewing,
                      entries: pipe(Object.values(viewingsCalendar), A.flatten),
                    })
                  ),
                  calendarSetup: {
                    calendarAvailability: calendar_availability,
                    newCalendarAvailability: pipe(
                      calendar_availability,
                      O.alt(() =>
                        O.some(
                          Types.calendarAvailabilityForProductType(product_type)
                        )
                      )
                    ),
                    submitAvailability: E.left(Http.ReqStatus.notAttempted()),
                    calendarNoticePeriodHours: pipe(
                      calendar_notice_period_hours,
                      O.alt(() =>
                        O.some(
                          Types.noticePeriodHoursForProductType(product_type)
                        )
                      )
                    ),
                    savedCalendarOverride: calendar_override,
                    calendarOverride: calendar_override,
                    calendarPaused: calendar_paused,
                  },
                  isCalendarSetupOpen: false,
                },
              })
            )
          ),
          cmd.none,
        ],
        [M._dealFeedbackReq]: () => {
          switch (config.route._tag) {
            case "Dashboard": {
              return [
                { ...model, feedbackModel: E.left("Loading") },
                pipe(
                  http.get(
                    `${API_BASE_URL}/myFlyp/feedback/${encodeURI(
                      config.route.propcode
                    )}`,
                    flow(
                      DealFeedback.decode,
                      E.mapLeft(IO.validationErrorsToString)
                    )
                  ),
                  Http.sendRequest(config.user)(
                    flow(M.Msg.dealFeedbackRes, config.wrapperMsg)
                  )
                ),
              ]
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._dealFeedbackRes]: result => {
          return pipe(
            result,
            E.match(
              status => {
                switch (status._tag) {
                  case "BadStatus": {
                    return status.response.status.code === 403
                      ? [
                          {
                            ...model,
                            tab: O.none,
                            feedbackModel: E.left("Not Authorized"),
                          },
                          cmd.none,
                        ]
                      : [{ ...model, feedbackModel: E.left("Error") }, cmd.none]
                  }
                  default: {
                    return [
                      { ...model, feedbackModel: E.left("Error") },
                      cmd.none,
                    ]
                  }
                }
              },
              response => {
                switch (config.route._tag) {
                  case "Dashboard": {
                    const propcode = config.route.propcode

                    return [
                      {
                        ...model,
                        feedbackModel: initFeedbackModel({
                          ...response,
                          CSCalendarUrl: pipe(
                            model.dashboard,
                            O.fromEither,
                            O.chain(d =>
                              pipe(
                                d.products,
                                A.findFirst(x => x.propertyCode === propcode),
                                O.chain(({ clientSuccess }) =>
                                  pipe(
                                    clientSuccess,
                                    O.chain(({ calendly }) =>
                                      pipe(
                                        calendly,
                                        O.map(calendlyUrl => calendlyUrl)
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          ),
                        }),
                      },
                      cmd.none,
                    ]
                  }
                  default: {
                    return [model, cmd.none]
                  }
                }
              }
            )
          )
        },
        [M._dealRentalsReq]: () => {
          switch (config.route._tag) {
            case "Dashboard": {
              return [
                { ...model, rentalsModel: E.left("Loading") },
                pipe(
                  http.get(
                    `${API_BASE_URL}/myFlyp/rentals/${encodeURI(
                      config.route.propcode
                    )}`,
                    flow(
                      DealRentals.decode,
                      E.mapLeft(IO.validationErrorsToString)
                    )
                  ),
                  Http.sendRequest(config.user)(
                    flow(M.Msg.dealRentalsRes, config.wrapperMsg)
                  )
                ),
              ]
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._dealRentalsRes]: result =>
          pipe(
            result,
            E.match(
              status => {
                switch (status._tag) {
                  case "BadStatus": {
                    return status.response.status.code === 403
                      ? [
                          {
                            ...model,
                            tab: O.none,
                            rentalsModel: E.left("Not Authorized"),
                          },
                          cmd.none,
                        ]
                      : [{ ...model, rentalsModel: E.left("Error") }, cmd.none]
                  }
                  default: {
                    return [
                      { ...model, rentalsModel: E.left("Error") },
                      cmd.none,
                    ]
                  }
                }
              },
              response => {
                const newModel: M.Model = {
                  ...model,
                  rentalsModel: E.of(initRentalsModel(response)),
                }

                return [
                  newModel,
                  cmd.of(
                    config.wrapperMsg(
                      M.Msg.rentalsMsg(RentalsModel.Msg.bookingsByMonthReq())
                    )
                  ),
                ]
              }
            )
          ),
        [M._overviewMsg]: overviewMsg => {
          switch (config.route._tag) {
            case "Dashboard": {
              return pipe(
                updateOverview.update(M.configOverview(config.route.propcode))(
                  overviewMsg
                )(model.overviewModel),
                tuple.bimap(
                  newCmd => cmd.map(config.wrapperMsg)(newCmd),
                  newOverviewModel => ({
                    ...model,
                    tab: O.some("overview"),
                    overviewModel: newOverviewModel,
                  })
                )
              )
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._viewingsMsg]: viewingsMgs => {
          switch (config.route._tag) {
            case "Dashboard": {
              return pipe(
                updateViewings.update(
                  M.configViewings(config.user, config.route.propcode)
                )(viewingsMgs)(model.viewingsModel),
                tuple.bimap(
                  newCmd => cmd.map(config.wrapperMsg)(newCmd),
                  newViewingsModel => ({
                    ...model,
                    tab: O.some("viewings"),
                    viewingsModel: newViewingsModel,
                  })
                )
              )
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._feedbackMsg]: feedbackMsg => {
          switch (config.route._tag) {
            case "Dashboard": {
              return pipe(
                updateFeedback.update(
                  M.configFeedback(config.user, config.route.propcode)
                )(feedbackMsg)(model.feedbackModel),
                tuple.bimap(
                  newCmd => cmd.map(config.wrapperMsg)(newCmd),
                  newFeedbackModel => ({
                    ...model,
                    tab: O.some("feedback"),
                    feedbackModel: newFeedbackModel,
                  })
                )
              )
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
        [M._rentalsMsg]: rentalsMsg => {
          switch (config.route._tag) {
            case "Dashboard": {
              return pipe(
                updateRentals.update(
                  M.configRentals(config.user, config.route.propcode)
                )(rentalsMsg)(model.rentalsModel),
                tuple.bimap(
                  newCmd => cmd.map(config.wrapperMsg)(newCmd),
                  newRentalsModel => ({
                    ...model,
                    tab: O.some("rentals"),
                    rentalsModel: newRentalsModel,
                  })
                )
              )
            }
            default: {
              return [model, cmd.none]
            }
          }
        },
      })
    )
  }
